'use client';

import * as React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ChevronDown } from 'lucide-react';
import { z } from 'zod';

import { cn, isRouteActive } from '../lib/utils';
import { If } from '../makerkit/if';
import type { NavigationConfigSchema } from '../makerkit/navigation-config.schema';
import { Trans } from '../makerkit/trans';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './collapsible';
import { useSidebar } from './sidebar-context';
import {
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
} from './sidebar-group';
import {
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
} from './sidebar-menu';
import { hasActiveChild } from './sidebar-navigation-helpers';
import { SidebarSeparator } from './sidebar-shell';
import { SidebarSubItems } from './sidebar-sub-items';

type SidebarConfig = z.infer<typeof NavigationConfigSchema>;

export function SidebarNavigation({
  config,
  getBadge,
}: React.PropsWithChildren<{
  config: SidebarConfig;
  getBadge?: (item: { path: string; label: string }) => React.ReactNode | null;
}>) {
  const currentPath = usePathname() ?? '';
  const { open } = useSidebar();

  return (
    <>
      {config.routes.map((item, index) => {
        const isLast = index === config.routes.length - 1;

        if ('divider' in item) {
          return <SidebarSeparator key={`divider-${index}`} />;
        }

        if ('children' in item) {
          // Single-leaf groups whose label repeats their only child render
          // bare: the item stands alone, no group label / collapse chrome
          // (navigation-architecture rule — an "Inbox" group containing only
          // "Inbox" must not repeat the label).
          const bareSingleLeaf =
            item.children.length === 1 &&
            item.children[0]?.label === item.label;
          const collapsible = item.collapsible && !bareSingleLeaf;

          // Check if any child route is active - if so, keep the group open
          const hasActiveChildRoute = hasActiveChild(
            item.children,
            currentPath,
          );
          // When a child becomes active, we want the group to open
          // Use key to force remount when active state changes to true
          const shouldBeOpen = hasActiveChildRoute || !item.collapsed;

          const Container = (props: React.PropsWithChildren) => {
            if (collapsible) {
              return (
                <Collapsible
                  key={
                    hasActiveChildRoute ? `${item.label}-active` : item.label
                  }
                  defaultOpen={shouldBeOpen}
                  className={'group/collapsible'}
                >
                  {props.children}
                </Collapsible>
              );
            }

            return props.children;
          };

          const ContentContainer = (props: React.PropsWithChildren) => {
            if (collapsible) {
              return <CollapsibleContent>{props.children}</CollapsibleContent>;
            }

            return props.children;
          };

          return (
            <Container key={`collapsible-${index}`}>
              <SidebarGroup key={item.label}>
                <If condition={!bareSingleLeaf}>
                  <If
                    condition={collapsible}
                    fallback={
                      <SidebarGroupLabel className={cn({ hidden: !open })}>
                        <Trans i18nKey={item.label} defaults={item.label} />
                      </SidebarGroupLabel>
                    }
                  >
                    <SidebarGroupLabel
                      className={cn({ hidden: !open })}
                      asChild
                    >
                      <CollapsibleTrigger>
                        <Trans i18nKey={item.label} defaults={item.label} />
                        <ChevronDown className="ml-auto h-4 w-4 shrink-0 transition-transform duration-200 ease-in-out group-data-[state=open]/collapsible:rotate-180" />
                      </CollapsibleTrigger>
                    </SidebarGroupLabel>
                  </If>
                </If>

                <If condition={item.renderAction}>
                  <SidebarGroupAction title={item.label}>
                    {item.renderAction}
                  </SidebarGroupAction>
                </If>

                <SidebarGroupContent>
                  <SidebarMenu>
                    <ContentContainer>
                      {item.children.map((child, childIndex) => {
                        const Container = (props: React.PropsWithChildren) => {
                          if ('collapsible' in child && child.collapsible) {
                            return (
                              <Collapsible
                                defaultOpen={!child.collapsed}
                                className={'group/collapsible'}
                              >
                                {props.children}
                              </Collapsible>
                            );
                          }

                          return props.children;
                        };

                        const ContentContainer = (
                          props: React.PropsWithChildren,
                        ) => {
                          if ('collapsible' in child && child.collapsible) {
                            return (
                              <CollapsibleContent>
                                {props.children}
                              </CollapsibleContent>
                            );
                          }

                          return props.children;
                        };

                        const TriggerItem = () => {
                          if ('collapsible' in child && child.collapsible) {
                            return (
                              <CollapsibleTrigger asChild>
                                <SidebarMenuButton tooltip={child.label}>
                                  <div
                                    className={cn('flex items-center gap-2', {
                                      'mx-auto w-full gap-0 [&>svg]:flex-1 [&>svg]:shrink-0':
                                        !open,
                                    })}
                                  >
                                    {child.Icon}
                                    <span
                                      className={cn(
                                        'transition-width w-auto transition-opacity duration-500',
                                        {
                                          'w-0 opacity-0': !open,
                                        },
                                      )}
                                    >
                                      <Trans
                                        i18nKey={child.label}
                                        defaults={child.label}
                                      />
                                    </span>

                                    <ChevronDown
                                      className={cn(
                                        'ml-auto size-4 transition-transform group-data-[state=open]/collapsible:rotate-180',
                                        {
                                          'hidden size-0': !open,
                                        },
                                      )}
                                    />
                                  </div>
                                </SidebarMenuButton>
                              </CollapsibleTrigger>
                            );
                          }

                          const path = 'path' in child ? child.path : '';
                          const end = 'end' in child ? child.end : false;

                          const isActive = isRouteActive(
                            path,
                            currentPath,
                            end,
                          );

                          const badge = path
                            ? (getBadge?.({ path, label: child.label }) ?? null)
                            : null;

                          return (
                            <>
                              <SidebarMenuButton
                                asChild
                                isActive={isActive}
                                tooltip={child.label}
                              >
                                <Link
                                  className={cn('flex items-center', {
                                    'mx-auto w-full gap-0! [&>svg]:flex-1':
                                      !open,
                                  })}
                                  href={path}
                                >
                                  {child.Icon}
                                  <span
                                    className={cn(
                                      'w-auto transition-opacity duration-300',
                                      {
                                        'w-0 opacity-0': !open,
                                      },
                                    )}
                                  >
                                    <Trans
                                      i18nKey={child.label}
                                      defaults={child.label}
                                    />
                                  </span>
                                </Link>
                              </SidebarMenuButton>

                              <If condition={badge}>{(node) => node}</If>
                            </>
                          );
                        };

                        return (
                          <Container key={`group-${index}-${childIndex}`}>
                            <SidebarMenuItem>
                              <TriggerItem />

                              <ContentContainer>
                                <If condition={child.children}>
                                  {(children) => (
                                    <SidebarSubItems
                                      currentPath={currentPath}
                                      open={open}
                                    >
                                      {children}
                                    </SidebarSubItems>
                                  )}
                                </If>
                              </ContentContainer>

                              <If condition={child.renderAction}>
                                <SidebarMenuAction>
                                  {child.renderAction}
                                </SidebarMenuAction>
                              </If>
                            </SidebarMenuItem>
                          </Container>
                        );
                      })}
                    </ContentContainer>
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>

              <If condition={!open && !isLast}>
                <SidebarSeparator />
              </If>
            </Container>
          );
        }
      })}
    </>
  );
}
